import { lazy, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import LoadingIndicator from '@components/LoadingIndicator/LoadingIndicator';
import OrderFormContainer from '@components/RequisitionCreate/OrderFormContainer/OrderFormContainer';
import { SOLO_CAMPAIGN_CODES } from '@constants/clinician';
import { ORDERS_AND_REPORTS } from '@constants/routes';
import { Campaign } from '@customTypes/campaign';
import { Context } from '@store/store';
import OrderingOptionSelect from './components/OrderingOptionSelect/OrderingOptionSelect';

const RequisitionJSONSchemaForm = lazy(
  () => import('@components/RequisitionCreate/RequisitionJSONSchemaForm/RequisitionJSONSchemaForm'),
);

export default function RequisitionCreate() {
  const navigate = useNavigate();
  const [state] = useContext(Context);

  // hasPartnershipOrdering = !IS_NATERA && useProfile.campaigns.length > 0
  const { hasPartnershipOrdering, userProfile } = state;

  // `selectedCampaign` is only set (not undefined) if the user has actively selected a campaign
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null | undefined>(undefined);

  // Use `selectedCampaign` if the user has chosen one
  // otherwise, select the first campaign that must be used for ordering
  let formCampaign = selectedCampaign;
  if (userProfile && selectedCampaign === undefined) {
    formCampaign = userProfile.campaigns.find(campaign => SOLO_CAMPAIGN_CODES.includes(campaign.campaign_code));
  }

  const moveToRequisitions = useCallback(() => {
    navigate(ORDERS_AND_REPORTS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onOrderingOptionSelect = (campaign: Campaign | null) => {
    setSelectedCampaign(campaign);
  };

  const breadcrumbs = useMemo(
    () => [
      {
        onClick: moveToRequisitions,
        children: 'Orders and Reports',
      },
      {
        active: true,
        children: 'New Order',
      },
    ],
    [moveToRequisitions],
  );

  if (!userProfile) {
    return (
      <OrderFormContainer breadcrumbs={breadcrumbs} title='New Order' onCancel={moveToRequisitions}>
        <LoadingIndicator />
      </OrderFormContainer>
    );
  }

  if (hasPartnershipOrdering && formCampaign === undefined) {
    return (
      <OrderFormContainer
        breadcrumbs={breadcrumbs}
        title='Your ordering options'
        subtitle='Select an option to proceed.'
        onCancel={moveToRequisitions}
      >
        <OrderingOptionSelect campaigns={userProfile.campaigns} onSelect={onOrderingOptionSelect} />
      </OrderFormContainer>
    );
  }

  return (
    <OrderFormContainer breadcrumbs={breadcrumbs} title='New Order' onCancel={moveToRequisitions}>
      <RequisitionJSONSchemaForm selectedCampaignCode={formCampaign?.campaign_code} />
    </OrderFormContainer>
  );
}
